import * as React from 'react';
import { Tooltip, Dropdown, Divider, Switch, Space, theme, type MenuProps } from 'antd';
import { FilterOutlined, MoreOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { OPEN_FILTER_MANAGEMENT_EVENT } from '@src/components/filter';
import apptracker from '@utils/sensor/apptracker';
import * as Events from '@src/utils/sensor/track';
import * as Store from '../store';
import SimpleFilter from '../SimpleFilter';
import AdvancedPanel from '../AdvancedPanel';
import { useReadOnlyRole } from '../../Management/hooks';
import { READ_ONLY_MESSAGE } from '../../constant';
import * as Styled from './styled';

const { useToken } = theme;

const AdvanceDropdownRender = () => <AdvancedPanel />;

const FilterDialogMain = () => {
  const { token } = useToken();
  const [filterValue] = Store.useFilterValue();
  const [filterStatus, setFilterStatus] = Store.useFilterStatus();
  const filterValueValidated = Store.useFilterValueValidated();
  const disabled = Store.useFilterDisabled();
  const advancedFilterEnabled = Store.useAdvancedFilterEnabled();
  const [conditionExpanded, setConditionExpanded] = Store.useRulesExpanded();
  const readonly = useReadOnlyRole();
  const handleKebabDropdownClick = React.useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      switch (key) {
        case 'save':
          setFilterStatus('simple-saving');
          break;
        case 'advanced':
          setFilterStatus('editing-advanced');
          break;
        case 'browse':
          window.dispatchEvent(
            new CustomEvent(OPEN_FILTER_MANAGEMENT_EVENT, {
              detail: {
                from: 'filter-dialog',
                enableApplyAction: true,
              },
            }),
          );
          apptracker(Events.BROWSER_FILTER_MANAGEMENT_ON_FILTER_BUILDER, {});
          break;
        default:
          break;
      }
    },
    [setFilterStatus],
  );
  const handleExpandChange = React.useCallback(
    (value: boolean) => {
      setConditionExpanded(value);
      apptracker(Events.FILTER_BUILDER_TOGGLE_EXPAND_CONDITIONS, { state: value });
    },
    [setConditionExpanded],
  );

  const advancedModeEnabled = ['editing-advanced', 'simple-saving'].includes(filterStatus);
  const kebabMenuItems = [
    {
      key: 'save',
      label: (
        <>
          Save this Filter
          {readonly && (
            <Tooltip title={readonly ? READ_ONLY_MESSAGE : undefined}>
              <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </Tooltip>
          )}
        </>
      ),
      disabled:
        filterValue.fid !== undefined ||
        !filterValue.rule.length ||
        readonly ||
        !filterValueValidated ||
        advancedFilterEnabled,
    },
    {
      key: 'advanced',
      label: 'Open Filter Builder',
      disabled: advancedFilterEnabled,
    },
    {
      key: 'browse',
      label: 'Browse Saved Filters',
    },
  ];

  return (
    <Styled.Container>
      <Dropdown
        disabled={disabled || advancedModeEnabled}
        menu={{
          items: kebabMenuItems,
          onClick: handleKebabDropdownClick,
        }}
        popupRender={(menu) => (
          <div
            style={{
              backgroundColor: token.colorBgElevated,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowSecondary,
            }}
          >
            {React.cloneElement(menu as React.ReactElement, { style: { boxShadow: 'none' } })}
            <Divider style={{ margin: 0 }} />
            <Space style={{ padding: 8 }}>
              <Switch
                defaultChecked={conditionExpanded}
                size="small"
                onChange={handleExpandChange}
              />
              Expand Conditions
            </Space>
          </div>
        )}
      >
        <Styled.DropdownHandler $disabled={disabled || advancedModeEnabled}>
          <FilterOutlined />
          <MoreOutlined />
        </Styled.DropdownHandler>
      </Dropdown>

      <Dropdown
        open={advancedModeEnabled}
        destroyOnHidden
        autoAdjustOverflow={false}
        placement="bottomLeft"
        popupRender={AdvanceDropdownRender}
      >
        <SimpleFilter />
      </Dropdown>
    </Styled.Container>
  );
};

export default React.memo(FilterDialogMain);
